import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useValidateRoomCode, useJoinStudyGroup } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion, AnimatePresence, springs, Pressable, FadeIn, PageTransition } from '../lib/motion';

export function StudyGroupJoinPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [joinError, setJoinError] = useState('');
  const [autoJoining, setAutoJoining] = useState(false);

  const { data, isLoading, error: fetchError } = useValidateRoomCode(
    user ? roomCode! : null,
  );
  const joinMutation = useJoinStudyGroup();

  const preview = data?.preview;

  // Auto-join when preview loads (user came from share link)
  useEffect(() => {
    if (!preview || autoJoining || joinMutation.isPending) return;
    setAutoJoining(true);
    joinMutation.mutateAsync(roomCode!)
      .then((result) => {
        navigate(`/study-groups/${result.group.id}/lobby`, { replace: true });
      })
      .catch((err: any) => {
        setJoinError(err.message || 'Failed to join. Please try again.');
        setAutoJoining(false);
      });
  }, [preview]);

  const handleJoin = async () => {
    setJoinError('');
    try {
      const result = await joinMutation.mutateAsync(roomCode!);
      navigate(`/study-groups/${result.group.id}/lobby`, { replace: true });
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join. Please try again.');
    }
  };

  // Loading states
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
        <div className="animate-spin w-8 h-8 border-[3px] border-squishy-teal/20 border-t-squishy-primary rounded-full mb-3" />
        <div className="text-sm">Checking authentication...</div>
      </div>
    );
  }

  if (!user) return null; // Will redirect

  if (isLoading || (autoJoining && !joinError)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-slate-500">
        <div className="animate-spin w-8 h-8 border-[3px] border-squishy-teal/20 border-t-squishy-primary rounded-full mb-3" />
        <div className="text-sm font-medium text-squishy-dark">
          {autoJoining ? 'Joining study group...' : 'Loading lobby...'}
        </div>
      </div>
    );
  }

  // Error states
  if (fetchError) {
    const errMsg = (fetchError as Error)?.message ?? '';

    let title = 'Could not find lobby';
    let description = 'This room code may be invalid. Double check and try again.';

    if (errMsg.includes('not found') || errMsg.includes('No study group')) {
      title = 'Lobby not found';
      description = 'This study group does not exist or has been removed.';
    } else if (errMsg.includes('full')) {
      title = 'Lobby is full';
      description = 'This study group has reached the maximum number of players.';
    } else if (errMsg.includes('already started')) {
      title = 'Session already started';
      description = 'This study group has already begun. You can no longer join.';
    }

    return (
      <PageTransition className="max-w-lg mx-auto pb-24 px-4">
        <div className="text-center py-16 space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <svg className="h-7 w-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold font-display text-squishy-dark">{title}</h2>
          <p className="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">{description}</p>
          <Pressable
            haptic="medium"
            onClick={() => navigate('/study-groups')}
            className="bg-squishy-primary text-white font-semibold px-6 py-3 rounded-2xl text-sm shadow-soft"
          >
            Go to Study Groups
          </Pressable>
        </div>
      </PageTransition>
    );
  }

  if (!preview) return null;

  return (
    <PageTransition className="max-w-lg mx-auto pb-24 px-4 space-y-5">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Pressable
          onClick={() => navigate('/study-groups')}
          className="flex h-10 w-10 -ml-2 items-center justify-center rounded-full text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Pressable>
        <h1 className="text-xl font-bold font-display text-squishy-dark">Join Study Group</h1>
      </div>

      {/* ── Lobby preview card ────────────────────────────────── */}
      <FadeIn>
        <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4 shadow-card">
          {/* Title */}
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-squishy-teal/10">
              <svg className="h-6 w-6 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="text-lg font-bold font-display text-squishy-dark">{preview.title}</div>
            <div className="text-xs text-slate-500 mt-1">{preview.sourceName}</div>
          </div>

          {/* Details */}
          <div className="space-y-2.5 border-t border-slate-100 pt-4">
            <DetailRow label="Questions" value={String(preview.questionCount)} />
            <DetailRow label="Timer" value={`${preview.timerSeconds} seconds`} />
            <DetailRow label="Players" value={`${preview.memberCount}/${preview.memberCap}`} />
          </div>
        </div>
      </FadeIn>

      {/* ── Join button ───────────────────────────────────────── */}
      <AnimatePresence>
        {joinError && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={springs.snappy}
            className="text-xs text-red-500"
          >
            {joinError}
          </motion.p>
        )}
      </AnimatePresence>

      <Pressable
        haptic="medium"
        onClick={handleJoin}
        disabled={joinMutation.isPending}
        className="w-full bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl disabled:opacity-50 shadow-soft transition-opacity"
      >
        {joinMutation.isPending ? 'Joining...' : 'Join Group'}
      </Pressable>
    </PageTransition>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{value}</span>
    </div>
  );
}
